import React, { useState, useEffect } from 'react';
import { ajouterRapport, rapportEnAttentePour } from '../utils/horsLigne';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import api, { stockAPI } from '../services/api';
import { expandPoles, compactPoles } from '../utils/poteaux';
import { useNavigate, useParams } from 'react-router-dom';
import { useFer, lireFer, ferEnKg, kgParBarre, kgFr } from '../utils/fer';

/** Saisie d'un fer : masse (t, kg, g) et/ou nombre de barres ; enregistré comme « 500 + 7 barres ». */
function FerSaisie({ cle, label, value, unite, onValue, onUnite, units, bg, table }) {
  const lu = () => {
    const q = lireFer(value, unite);
    if (!q) return { masse: '', barres: '' };
    const f = { t: 1000, kg: 1, g: 0.001 }[unite] || 1;
    return { masse: q.masseKg ? String(Math.round((q.masseKg / f) * 1000) / 1000).replace('.', ',') : '', barres: q.barres ? String(q.barres) : '' };
  };
  const [v, setV] = useState(lu);
  useEffect(() => { // valeur chargée depuis le serveur (modification d'un rapport)
    const c = lu(); if (c.masse !== v.masse || c.barres !== v.barres) setV(c);
  }, [value]); // eslint-disable-line
  const maj = (patch) => {
    const n = { ...v, ...patch }; setV(n);
    const m = n.masse.trim(); const b = n.barres.trim();
    onValue(m && b ? `${m} + ${b} barres` : m || (b ? `${b} barres` : ''));
  };
  const kpb = kgParBarre(cle, null, table);
  const kg = value ? ferEnKg(value, unite, cle, null, table) : null;
  const q = value ? lireFer(value, unite) : null;
  return (
    <Box sx={{ flex: 1, minWidth: 0, borderRadius: 1, bgcolor: value ? bg : 'transparent', p: value ? 0.5 : 0 }}>
      {label && <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>{label}</Typography>}
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        <TextField size="small" label="Masse" value={v.masse} sx={{ flex: 1.3 }}
          onChange={(e) => maj({ masse: e.target.value.replace(/[^\d.,]/g, '') })} inputProps={{ inputMode: 'decimal', 'aria-label': `${cle} masse` }} />
        <FormControl size="small" sx={{ minWidth: 64 }}>
          <Select value={unite} onChange={(e) => onUnite(e.target.value)} inputProps={{ 'aria-label': `Unité ${cle}` }}>
            {units.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Barres" value={v.barres} sx={{ flex: 1 }}
          onChange={(e) => maj({ barres: e.target.value.replace(/[^\d]/g, '') })} inputProps={{ inputMode: 'numeric', 'aria-label': `${cle} barres` }} />
      </Box>
      <Typography sx={{ fontSize: 11.5, mt: 0.25, color: q?.barres && kg === null ? '#B4232A' : 'text.secondary', minHeight: 16 }}>
        {q && (q.barres || unite !== 'kg')
          ? (kg !== null ? `= ${kgFr(kg)}${q.barres ? ` (${q.barres} barre${q.barres > 1 ? 's' : ''} × ${String(kpb).replace('.', ',')} kg)` : ''}` : 'Poids de la barre non renseigné (Paramètres)')
          : kpb ? `1 barre = ${String(kpb).replace('.', ',')} kg` : ''}
      </Typography>
    </Box>
  );
}

const PBA_TYPES = [
  '9AR150', '9AR300', '9AR400', '9AR650', 
  '12AR400', '12AR650', '12B1250', 
  '12B1600', '12B2000', '10B2000'
];

const MATERIALS = [
  // Fer local : 6, 8, 10 — fer importé : 10, 12, 14, 16, 20 (le fer 18 n'est pas utilisé ;
  // une ancienne saisie d'une variante retirée reste affichée pour pouvoir la corriger)
  { name: 'Fer6', isFer: true, origines: ['LOCAL'] },
  { name: 'Fer8', isFer: true, origines: ['LOCAL'] },
  { name: 'Fer10', isFer: true, origines: ['IMPORTE', 'LOCAL'] },
  { name: 'Fer12', isFer: true, origines: ['IMPORTE'] },
  { name: 'Fer14', isFer: true, origines: ['IMPORTE'] },
  { name: 'Fer16', isFer: true, origines: ['IMPORTE'] },
  { name: 'Fer18', isFer: true, origines: [] },
  { name: 'Fer20', isFer: true, origines: ['IMPORTE'] },
  { name: 'Étriers façonnés', unit: 'unité' },
  { name: 'Sacs de ciment', unit: 'sacs', label: 'Ciment 42,5 R' },
  { name: 'Béton 3/8', unit: 'm3' },
  { name: 'Béton 8/16', unit: 'm3' },
  { name: 'Béton 0/3', unit: 'm3' },
  { name: 'Béton 16/25', unit: 'm3' },
  { name: 'Sable', unit: 'm3' }
];

const FER_UNITS = ['t', 'kg', 'g'];

const PERSONNEL_TYPES = [
  'Production', 'Soudeur', 'Ferrailleur', 
  'Ouvrier', 'Maçon', 'Manœuvre'
];

const DailyReport = () => {
  const ferTable = useFer();
  const navigate = useNavigate();
  const { id: editId } = useParams(); // présent : modification d'un rapport existant (administrateur, responsable)
  const [editInfo, setEditInfo] = useState(null);
  const [formData, setFormData] = useState({
    report_date: new Date().toISOString().split('T')[0],
    employee_name: '',
    employee_firstname: '',
    pba_produced: {},
    materials_used: {},
    materials_units: {},
    materials_origine: {},
    armatures_made: {},
    personnel: {},
    observations: ''
  });
  
  const [preview, setPreview] = useState(false);
  // Numéros des poteaux produits : type -> texte saisi (proposé automatiquement à partir du dernier numéro connu)
  const [poles, setPoles] = useState({});
  const [polesEdited, setPolesEdited] = useState({});
  const [polesHint, setPolesHint] = useState({});
  // Modification : chargement du rapport existant
  useEffect(() => {
    if (!editId) return;
    stockAPI.getReport(editId).then((r) => {
      const d = r.data.report || r.data;
      const obj = (v) => (typeof v === 'string' ? (() => { try { return JSON.parse(v); } catch { return {}; } })() : (v || {}));
      const pba = obj(d.pba_produced);
      delete pba.references;
      setFormData({
        report_date: String(d.report_date).slice(0, 10), employee_name: d.employee_name || '', employee_firstname: d.employee_firstname || '',
        pba_produced: pba, materials_used: obj(d.materials_used), materials_units: obj(d.materials_units), materials_origine: obj(d.materials_origine),
        armatures_made: obj(d.armatures_made), personnel: obj(d.personnel), observations: d.observations || '',
      });
      const pt = obj(d.poteaux);
      setPoles(Object.fromEntries(Object.entries(pt).map(([t, l]) => [t, compactPoles(l)])));
      setPolesEdited(Object.fromEntries(Object.keys(pt).map((t) => [t, true])));
      setEditInfo({ numero: `RAP-${String(d.id).padStart(5, '0')}`, auteur: [d.employee_firstname, d.employee_name].filter(Boolean).join(' ') || d.created_by_username });
    }).catch((e) => setError(e.response?.data?.error || 'Rapport introuvable'));
  }, [editId]);
  const produced = Object.entries(formData.pba_produced || {}).filter(([, q]) => Number(q) > 0);
  const producedKey = produced.map(([t, q]) => `${t}:${q}`).join(',');
  useEffect(() => {
    produced.forEach(([type, qty]) => {
      if (polesEdited[type]) return;
      api.get('/poteaux/suggestion', { params: { type, qty } })
        .then((r) => {
          setPoles((p) => (polesEdited[type] ? p : { ...p, [type]: r.data.texte }));
          setPolesHint((h) => ({ ...h, [type]: r.data.debut === null ? 'Aucun numéro connu pour ce type : saisissez les numéros marqués sur les poteaux.' : '' }));
        })
        .catch(() => {});
    });
  }, [producedKey]); // eslint-disable-line
  // Numéros facultatifs : ceux qui manquent s'attribuent plus tard (Identifiants poteaux → Stock à numéroter)
  const poleCheck = (type, qty) => {
    const { list, dups, error: err } = expandPoles(poles[type]);
    if (err) return { ok: false, msg: err };
    if (dups.length) return { ok: false, msg: `En double : ${dups.slice(0, 5).join(', ')}` };
    if (list.length > Number(qty)) return { ok: false, msg: `${list.length} numéro(s) pour ${qty} poteau(x) produit(s) : trop de numéros` };
    if (list.length === Number(qty)) return { ok: true, level: 'success', msg: `${list.length} numéro(s) = ${qty} produit(s) ✓` };
    const reste = Number(qty) - list.length;
    return { ok: true, level: 'warning', msg: list.length
      ? `${list.length} numéro(s) pour ${qty} : ${reste} poteau(x) à numéroter plus tard`
      : 'Sans numéro : à attribuer plus tard (Identifiants poteaux → Stock à numéroter)' };
  };
  const polesOk = produced.every(([t, q]) => poleCheck(t, q).ok);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [conso, setConso] = useState(null); // sorties du stock intrants faites par le dernier rapport


  // Pas de chargement automatique



  const handleInputChange = (section, key, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value || undefined
      }
    }));
  };

  // Fer : « Fer12 » = importé, « Fer12L » = local (deux quantités possibles pour un même diamètre)
  const origineFer = (material) => (/^fer\d+l$/i.test(material) ? 'LOCAL' : 'IMPORTE');

  const handleUnitChange = (material, unit) => {
    setFormData(prev => ({
      ...prev,
      materials_units: {
        ...prev.materials_units,
        [material]: unit
      }
    }));
  };

  // Un seul rapport par date : on prévient dès le choix de la date
  const [dejaSaisi, setDejaSaisi] = useState(null);
  useEffect(() => {
    if (!formData.report_date) return;
    api.get('/reports/existe', { params: { date: formData.report_date } })
      .then((r) => setDejaSaisi(r.data.exists && String(r.data.id) !== String(editId || '') ? r.data : null))
      .catch(() => setDejaSaisi(!editId && rapportEnAttentePour(formData.report_date) ? { auteur: 'vous (en attente d\'envoi sur cet appareil)' } : null));
  }, [formData.report_date, success]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      console.log('Envoi du rapport:', formData);
      const pole_numbers = Object.fromEntries(produced.map(([t]) => [t, expandPoles(poles[t]).list]));
      const materials_origine = Object.fromEntries(Object.entries(formData.materials_used || {})
        .filter(([k, v]) => /^fer/i.test(k) && v && String(v).trim()).map(([k]) => [k, origineFer(k)]));
      if (editId) {
        const r = await stockAPI.updateReport(editId, { ...formData, materials_origine, pole_numbers });
        setPreview(false);
        const al = r.data?.consommation?.alertes || [];
        navigate('/reports-history', { state: { info: `Rapport ${editInfo?.numero || ''} modifié${r.data?.consommation?.applique ? ' — sorties du stock intrants recalculées' : ''}${al.length ? `. Attention : ${al.join(' ; ')}` : ''}` } });
        return;
      }
      const payload = { ...formData, materials_origine, pole_numbers };
      let response;
      try {
        if (!navigator.onLine) throw Object.assign(new Error('hors connexion'), { horsLigne: true });
        response = await stockAPI.createReport(payload);
      } catch (e) {
        if (!e.horsLigne && e.response) throw e;
        // Pas de réseau : le rapport est gardé sur l'appareil et partira tout seul au retour de la connexion
        ajouterRapport(payload);
        response = null;
      }
      setSuccess(response ? 'Rapport envoyé avec succès'
        : 'Pas de réseau : rapport enregistré sur cet appareil. Il sera envoyé automatiquement dès que la connexion reviendra (ne videz pas les données du navigateur d\'ici là).');
      setConso(response?.data?.consommation || null);
      setPreview(false);
      setPoles({}); setPolesEdited({}); setPolesHint({});
      // Réinitialiser le formulaire
      setFormData({
        report_date: new Date().toISOString().split('T')[0],
        employee_name: '',
        employee_firstname: '',
        pba_produced: {},
        materials_used: {},
        materials_units: {},
        materials_origine: {},
        armatures_made: {},
        personnel: {},
        observations: ''
      });
    } catch (error) {
      console.error('Erreur envoi rapport:', error);
      setError(error.response?.data?.error || 'Erreur lors de l\'envoi du rapport');
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = () => {
    const { report_date, employee_name, employee_firstname, pba_produced, materials_used, armatures_made, personnel, observations } = formData;
    
    let preview = `Rapport du ${new Date(report_date).toLocaleDateString('fr-FR')} – ${employee_firstname} ${employee_name}\n\n`;
    
    // PBA produits
    const pbaEntries = Object.entries(pba_produced || {}).filter(([_, value]) => value > 0);
    if (pbaEntries.length > 0) {
      pbaEntries.forEach(([type, qty]) => {
        const lst = expandPoles(poles[type]).list;
        const nums = compactPoles(lst);
        const reste = Number(qty) - lst.length;
        preview += `${type} = ${qty}${nums ? `  (n° ${nums})` : ''}${reste > 0 ? `  — ${reste} sans numéro (à attribuer)` : ''}\n`;
      });
      const totalPBA = pbaEntries.reduce((sum, [_, qty]) => sum + parseInt(qty), 0);
      preview += `Total PBA : ${totalPBA}\n\n`;
    }
    
    // Matériaux utilisés
    const materialEntries = Object.entries(materials_used || {}).filter(([_, value]) => value && typeof value === 'string' && value.trim());
    if (materialEntries.length > 0) {
      preview += 'Matériaux utilisés : ';
      preview += materialEntries.map(([material, qty]) => {
        const unit = formData.materials_units[material] || 'kg';
        const isFer = material.toLowerCase().includes('fer');
        const nomFer = `${material.replace(/l$/i, '').toLowerCase()} ${origineFer(material) === 'LOCAL' ? 'local' : 'importé'}`;
        if (isFer) {
          // Si contient des barres, remplacer l'ordre
          if (qty.includes('+') && qty.toLowerCase().includes('barre')) {
            const formattedQty = qty.replace(/(\d+)\s*\+\s*(\d+)\s*barres?/i, `$1${unit} + $2barres`);
            return `${formattedQty} ${nomFer}`;
          } else {
            // Sinon, juste ajouter l'unité
            return `${qty}${unit} ${nomFer}`;
          }
        }
        return `${qty} ${material.toLowerCase()}`;
      }).join(', ');
      preview += '\n\n';
    }
    
    // Armatures façonnées
    const armatureEntries = Object.entries(armatures_made || {}).filter(([_, value]) => value > 0);
    if (armatureEntries.length > 0) {
      preview += 'Armatures façonnées :\n';
      armatureEntries.forEach(([type, qty]) => {
        preview += `${qty} armature${qty > 1 ? 's' : ''} ${type}\n`;
      });
      const totalArmatures = armatureEntries.reduce((sum, [_, qty]) => sum + parseInt(qty), 0);
      preview += `Total armatures : ${totalArmatures}\n\n`;
    }
    
    // Personnel mobilisé
    const personnelEntries = Object.entries(personnel || {}).filter(([_, value]) => value > 0);
    if (personnelEntries.length > 0) {
      preview += 'Personnel mobilisé :\n';
      personnelEntries.forEach(([type, qty]) => {
        preview += `${qty} ${type.toLowerCase()}${qty > 1 ? 's' : ''}\n`;
      });
      const totalPersonnel = personnelEntries.reduce((sum, [_, qty]) => sum + parseInt(qty), 0);
      preview += `Total personnel : ${totalPersonnel}\n\n`;
    }
    
    // Observations
    if (observations && typeof observations === 'string' && observations.trim()) {
      preview += `Observations : ${observations}`;
    }
    
    return preview;
  };

  return (
    <Box>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ flex: 1 }}>
            {editId ? `Modifier le rapport ${editInfo?.numero || ''}` : 'Rapport Journalier de Production'}
          </Typography>
          {editId && <Button onClick={() => navigate('/reports-history')}>Annuler</Button>}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {dejaSaisi && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Un rapport existe déjà pour le {formData.report_date.split('-').reverse().join('/')} (saisi par {dejaSaisi.auteur}).
            Un seul rapport par jour : choisissez une autre date ou demandez à un responsable de le corriger.
          </Alert>
        )}

        {editId && editInfo && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Modification du rapport de {editInfo.auteur}. Le stock suit les changements de production (lots, mouvements, numéros des poteaux).
            Les poteaux déjà livrés ou sortis ne peuvent pas être retirés.
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => { setSuccess(''); setConso(null); }}>
            {success}
            {conso?.applique && conso.sorties.length > 0 && (
              <Box sx={{ mt: 0.5, fontSize: 13.5 }}>
                Sorties du stock intrants : {conso.sorties.map((x) => `${x.intrant} ${Number(x.quantite).toLocaleString('fr-FR', { maximumFractionDigits: 3 })} ${x.unite}`).join(' · ')}
              </Box>
            )}
          </Alert>
        )}
        {success && conso?.alertes?.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <b>Stock intrants à vérifier :</b>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{conso.alertes.map((a) => <li key={a}>{a}</li>)}</ul>
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          {/* Informations personnelles */}
          <Typography variant="h6" gutterBottom>
            Informations personnelles
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Nom *"
                value={formData.employee_name}
                onChange={(e) => setFormData(prev => ({ ...prev, employee_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Prénom *"
                value={formData.employee_firstname}
                onChange={(e) => setFormData(prev => ({ ...prev, employee_firstname: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Date *"
                value={formData.report_date}
                onChange={(e) => setFormData(prev => ({ ...prev, report_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Quantité de PBA produits */}
          <Typography variant="h6" gutterBottom>
            Quantité de PBA produits
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {PBA_TYPES.map(type => (
              <Grid item xs={6} md={3} key={type}>
                <TextField
                  fullWidth
                  label={`${type} - Quantité`}
                  type="number"
                  value={(formData.pba_produced && formData.pba_produced[type]) || ''}
                  onChange={(e) => handleInputChange('pba_produced', type, parseInt(e.target.value) || 0)}
                />
              </Grid>
            ))}
          </Grid>

          {produced.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Numéros des poteaux produits</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Un numéro par poteau, tel que marqué à l'usine. Plage : « 30000 à 30049 » ; liste : « 30000, 30002, 30005 ».
                La suite du dernier numéro enregistré est proposée : corrigez-la si besoin. Facultatif : « Plus tard » envoie le rapport
                sans numéros, ils s'attribuent ensuite dans Identifiants poteaux → Stock à numéroter.
              </Typography>
              <Grid container spacing={2}>
                {produced.map(([type, qty]) => {
                  const c = poleCheck(type, qty);
                  return (
                    <Grid item xs={12} md={6} key={type}>
                      <TextField fullWidth size="small" label={`${type} — ${qty} poteau(x)`} value={poles[type] || ''}
                        placeholder="ex. 30000 à 30049" error={!c.ok && !!poles[type]}
                        helperText={(!poles[type] && !polesEdited[type] && polesHint[type]) || c.msg}
                        FormHelperTextProps={{ sx: { color: c.ok ? `${c.level}.main` : undefined } }}
                        InputProps={{ endAdornment: poles[type] ? (
                          <Button size="small" sx={{ whiteSpace: 'nowrap', minWidth: 0 }}
                            onClick={() => { setPoles((p) => ({ ...p, [type]: '' })); setPolesEdited((x) => ({ ...x, [type]: true })); }}>
                            Plus tard
                          </Button>
                        ) : null }}
                        onChange={(e) => { setPoles((p) => ({ ...p, [type]: e.target.value })); setPolesEdited((x) => ({ ...x, [type]: true })); }} />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Matériaux utilisés */}
          <Typography variant="h6" gutterBottom>
            Matériaux utilisés
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {MATERIALS.map((material) => ({ ...material,
              variantes: material.isFer ? [{ key: material.name, label: 'Importé', o: 'IMPORTE', bg: '#E9EEF7' }, { key: `${material.name}L`, label: 'Local', o: 'LOCAL', bg: '#E8F3EC' }]
                .filter((v) => material.origines.includes(v.o) || (formData.materials_used && formData.materials_used[v.key])) : null }))
              .filter((material) => !material.isFer || material.variantes.length)
              .map(material => (
              <Grid item xs={material.isFer ? 12 : 6} md={material.isFer ? (material.variantes.length > 1 ? 6 : 3) : 3} key={material.name}>
                {material.isFer ? (
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>{material.name.replace(/^Fer(\d+)/, 'Fer de $1')}{material.variantes.length === 1 ? ` ${material.variantes[0].o === 'LOCAL' ? 'local' : 'importé'}` : ''}</Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                      {material.variantes.map((o) => (
                        <FerSaisie key={o.key} cle={o.key} label={material.variantes.length > 1 ? o.label : ''} bg={o.bg} table={ferTable} units={FER_UNITS}
                          value={(formData.materials_used && formData.materials_used[o.key]) || ''}
                          unite={(formData.materials_units && formData.materials_units[o.key]) || 'kg'}
                          onValue={(val) => handleInputChange('materials_used', o.key, val)} onUnite={(u) => handleUnitChange(o.key, u)} />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <TextField
                    fullWidth
                    label={`${material.label || material.name} (${material.unit})`}
                    value={(formData.materials_used && formData.materials_used[material.name]) || ''}
                    onChange={(e) => handleInputChange('materials_used', material.name, e.target.value)}
                  />
                )}
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Armatures façonnées */}
          <Typography variant="h6" gutterBottom>
            Armatures façonnées
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {PBA_TYPES.map(type => (
              <Grid item xs={6} md={3} key={type}>
                <TextField
                  fullWidth
                  label={type}
                  type="number"
                  value={(formData.armatures_made && formData.armatures_made[type]) || ''}
                  onChange={(e) => handleInputChange('armatures_made', type, parseInt(e.target.value) || 0)}
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Personnel de production */}
          <Typography variant="h6" gutterBottom>
            Personnel de production
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {PERSONNEL_TYPES.map(type => (
              <Grid item xs={6} md={4} key={type}>
                <TextField
                  fullWidth
                  label={type}
                  type="number"
                  value={(formData.personnel && formData.personnel[type]) || ''}
                  onChange={(e) => handleInputChange('personnel', type, parseInt(e.target.value) || 0)}
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Observations */}
          <Typography variant="h6" gutterBottom>
            Observations
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Commentaires"
            value={formData.observations}
            onChange={(e) => setFormData(prev => ({ ...prev, observations: e.target.value }))}
            sx={{ mb: 3 }}
          />

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => setPreview(true)}
              disabled={!formData.employee_name || !formData.employee_firstname}
            >
              Prévisualiser
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !!dejaSaisi || !polesOk || !formData.employee_name || !formData.employee_firstname}
            >
              {editId ? 'Enregistrer les modifications' : 'Envoyer le rapport'}
            </Button>
          </Box>
        </Paper>

        {/* Dialog de prévisualisation */}
        <Dialog open={preview} onClose={() => setPreview(false)} maxWidth="md" fullWidth>
          <DialogTitle>Prévisualisation du rapport</DialogTitle>
          <DialogContent>
            <Card>
              <CardContent>
                <Typography component="pre" sx={{ whiteSpace: 'pre-line', fontFamily: 'monospace' }}>
                  {generatePreview()}
                </Typography>
              </CardContent>
            </Card>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreview(false)}>Modifier</Button>
            <Button variant="contained" onClick={handleSubmit} disabled={loading || !!dejaSaisi || !polesOk}>
              {editId ? 'Confirmer les modifications' : 'Confirmer et envoyer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default DailyReport;